import { Link } from "react-router-dom";
import {
  AlertTriangle, CalendarClock, Bell, ListChecks, ArrowRight, Activity,
} from "lucide-react";

/**
 * Compact replacement for the old large dashboard cards (Risk Flags, Schedule
 * Conflicts, Parent Action Items, Upcoming Deadlines). Sources:
 *   - briefingPreview.risk_count / conflict_count
 *   - pendingTasks (priority Critical/Important → "action items")
 *   - calendarSuggestions filtered to non-confirmed → "upcoming deadlines"
 */
export default function FamilyIntelligence({
  briefingPreview, pendingTasks = [], calendarSuggestions = [],
}) {
  const risks = briefingPreview?.risk_count ?? 0;
  const conflicts = briefingPreview?.conflict_count ?? 0;
  const actionItems = pendingTasks.filter(
    (t) => !t.completed && (t.priority === "Critical" || t.priority === "Important"),
  );
  const deadlineEvents = calendarSuggestions
    .filter((s) => !s.confirmed && s.event_date)
    .map((s) => ({ title: s.title, when: s.event_date, child: s.child_name, kind: "event" }));
  const deadlineTasks = pendingTasks
    .filter((t) => !t.completed && t.due_date)
    .map((t) => ({ title: t.title, when: t.due_date, child: t.child_name, kind: "task" }));
  const allDeadlines = [...deadlineEvents, ...deadlineTasks]
    .sort((a, b) => (a.when || "").localeCompare(b.when || ""));
  const deadlinesCount = allDeadlines.length;
  const deadlinesPreview = allDeadlines.slice(0, 5);

  return (
    <section data-testid="family-intelligence" className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <p className="label-overline flex items-center gap-2"><Activity className="w-3 h-3" /> Family intelligence</p>
          <h2 className="font-display text-2xl text-[var(--fcc-ink)] mt-1.5">What&apos;s worth keeping an eye on</h2>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <IntelRow
          testid="intel-risk-flags"
          icon={AlertTriangle}
          tint="bg-[var(--fcc-red-soft)]"
          label="Risk flags"
          value={risks}
          caption={risks === 0 ? "Nothing burning. Enjoy the calm." : "Things that could go wrong if ignored."}
          to="/app/briefing"
        />
        <IntelRow
          testid="intel-conflicts"
          icon={CalendarClock}
          tint="bg-[var(--fcc-peach)]"
          label="Schedule conflicts"
          value={conflicts}
          caption={conflicts === 0 ? "No double-bookings this week." : "Double-bookings detected — review them."}
          to="/app/briefing"
        />
        <IntelRow
          testid="intel-action-items"
          icon={Bell}
          tint="bg-[var(--fcc-green-soft)]"
          label="Parent action items"
          value={actionItems.length}
          caption={
            actionItems.length === 0
              ? "All caught up."
              : actionItems.slice(0, 3).map((t) => t.title).join(" · ")
          }
          to="/app/tasks"
        />
        <IntelRow
          testid="intel-deadlines"
          icon={ListChecks}
          tint="bg-[var(--fcc-noah)]"
          label="Upcoming deadlines"
          value={deadlinesCount}
          caption={
            deadlinesCount === 0
              ? "No deadlines on the horizon."
              : deadlinesPreview.map((d) => `${(d.when || "").slice(5, 10)} · ${d.title}`).join(" · ")
          }
          to="/app/tasks"
        />
      </div>
    </section>
  );
}

function IntelRow({ testid, icon: Icon, tint, label, value, caption, to }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="fcc-card p-4 sm:p-5 flex items-center gap-4 hover:bg-[var(--fcc-cream-2)] transition group"
    >
      <span className={`inline-flex w-11 h-11 rounded-xl items-center justify-center shrink-0 ${tint}`}>
        <Icon className="w-4 h-4 text-[var(--fcc-green)]" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="label-overline">{label}</p>
        <div className="flex items-baseline gap-3 mt-0.5">
          <span className="font-display text-3xl text-[var(--fcc-ink)] leading-none">{value}</span>
          <span className="text-xs text-[var(--fcc-ink-soft)] truncate">{caption}</span>
        </div>
      </div>
      <ArrowRight className="w-4 h-4 text-[var(--fcc-muted)] group-hover:text-[var(--fcc-green)] transition shrink-0" />
    </Link>
  );
}
